<script setup>
import { convertToInr } from "@/services/common-services";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";
import ManageSIPDialog from '@/components/dashboard/ManageSIPDialog.vue';

const store = useUserStore();
const isAccordionExpanded = ref(false);
const isCalculationsAccordionExpanded = ref(false);

const data = computed(() => store.financialStatement || null);
console.log("data.value", data.value);
const health = computed(() => data.value?.financialHealth || {});
console.log('health.value', health.value);
const expenseData = computed(() => data.value?.customerExpenses || []);
const incomeData = computed(() => data.value?.customerIncome || []);
const fundstatus = computed(() => data.value?.emergencyFundStatus || null);
const insuranceData = computed(() => data.value?.insuranceDetails || []);
const assetData = computed(() => data.value?.customerAssets || []);
const liabilityData = computed(() => data.value?.customerLiabilities || []);

// Calculate recommended emergency fund balance: 12 * (Monthly Expenses + Monthly EMI)
// EMI should not exceed outstanding amount for that liability
const recommendedEmergencyFund = computed(() => {
  const monthlyExpenses = totalExpense.value;

  // Calculate total monthly EMI, ensuring it doesn't exceed outstanding amount
  const monthlyEMI = liabilityData.value?.reduce((total, item) => {
    const emiAmount = item.emiAmount || 0;
    const amtOutstanding = item.amtOutstanding || 0;

    // EMI should not exceed outstanding amount
    const validEMI = Math.min(emiAmount, amtOutstanding);
    return total + validEMI;
  }, 0) || 0;

  return 12 * (monthlyExpenses + monthlyEMI);
});

// Calculate monthly EMI for display in accordion
const monthlyEMI = computed(() => {
  return liabilityData.value?.reduce((total, item) => {
    const emi = item.emiAmount || 0;
    const outstanding = item.amtOutstanding || 0;

    // Only include EMI if it doesn't exceed outstanding amount
    if (emi > 0 && emi <= outstanding) {
      return total + emi;
    }
    return total;
  }, 0) || 0;
});

// Calculate emergency fund percentage
const emergencyFundPercentage = computed(() => {
  if (!fundstatus.value?.currentBalance || recommendedEmergencyFund.value === 0) return 0;
  return Math.min(100, (fundstatus.value.currentBalance / recommendedEmergencyFund.value) * 100);
});

// Determine emergency fund dial color
const emergencyFundColor = computed(() => {
  const percentage = emergencyFundPercentage.value;
  if (percentage >= 90) return "var(--an-success)";
  if (percentage >= 70) return "#f59e0b"; // amber
  return "var(--an-error)";
});

// Filter out zero values
const filteredExpenseData = computed(() =>
  expenseData.value?.filter(item => item.expAmt > 0) || []
);

const filteredIncomeData = computed(() =>
  incomeData.value?.filter(item => item.incomeAmt > 0) || []
);

const totalExpense = computed(() =>
  filteredExpenseData.value?.reduce((total, item) => total + item.expAmt, 0) || 0
);

const totalIncome = computed(() =>
  filteredIncomeData.value?.reduce((total, item) => total + item.incomeAmt, 0) || 0
);

const totalAsset = computed(() =>
  assetData.value?.filter(item => item.assetValue > 0)
    .reduce((total, item) => total + item.assetValue, 0) || 0
);

const totalLiability = computed(() =>
  liabilityData.value?.filter(item => item.amtOutstanding > 0)
    .reduce((total, item) => total + item.amtOutstanding, 0) || 0
);

// Calculate current age from DOB
const currentAge = computed(() => {
  const dob = store.dashboardData?.dob;
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
});

// Calculate years of dependency (65 - current age)
const yearsOfDependency = computed(() => {
  if (!currentAge.value) return 0;
  return Math.max(0, 65 - currentAge.value);
});

// Calculate total goals target amount (sum of all targetFV)
const totalGoalsTarget = computed(() => {
  const goalSummary = store.goalSummary;
  if (!goalSummary?.modes?.perGoalSIP?.goals) return 0;
  return goalSummary.modes.perGoalSIP.goals.reduce((sum, goal) => {
    return sum + (goal.targetFV || 0);
  }, 0);
});

// Calculate recommended Term Life coverage
const recommendedTermLifeCoverage = computed(() => {
  const annualExpenses = totalExpense.value * 12;
  const dependencyAmount = annualExpenses * yearsOfDependency.value;
  const liabilities = totalLiability.value;
  const goalsTarget = totalGoalsTarget.value;
  const existingAssets = totalAsset.value;

  return Math.max(0, dependencyAmount + liabilities + goalsTarget - existingAssets);
});

// Calculate recommended Health coverage
const recommendedHealthCoverage = computed(() => {
  const maritalStatus = store.dashboardData?.maritalStatus || 'Single';
  return maritalStatus === 'Single' ? 1000000 : 1500000;
});

// Get current insurance coverage
const currentTermLifeCoverage = computed(() => {
  const termPolicy = insuranceData.value.find(ins =>
    ins.insuranceType === 'Term Life' || ins.insuranceType === 'Term'
  );
  return termPolicy?.coverage || 0;
});

const currentHealthCoverage = computed(() => {
  const healthPolicy = insuranceData.value.find(ins => ins.insuranceType === 'Health');
  return healthPolicy?.coverage || 0;
});

// Check adequacy
const isTermLifeAdequate = computed(() => {
  if (!currentTermLifeCoverage.value) return false;
  return currentTermLifeCoverage.value >= recommendedTermLifeCoverage.value;
});

const isHealthAdequate = computed(() => {
  if (!currentHealthCoverage.value) return false;
  return currentHealthCoverage.value >= recommendedHealthCoverage.value;
});

// SIP Management Dialog state
const isSIPDialogVisible = ref(false);

const openSIPDialog = () => {
  isSIPDialogVisible.value = true;
};

const closeSIPDialog = () => {
  isSIPDialogVisible.value = false;
};

// Get current SIP values from financial statement (from cashSavings array)
const currentMonthlySIP = computed(() => {
  const cashSavings = data.value?.cashSavings;
  if (!cashSavings || !Array.isArray(cashSavings) || cashSavings.length === 0) return 0;
  return cashSavings[0]?.monthlySIP || 0;
});

const currentWeightedAvgReturn = computed(() => {
  const cashSavings = data.value?.cashSavings;
  if (!cashSavings || !Array.isArray(cashSavings) || cashSavings.length === 0) return 0;
  return cashSavings[0]?.weightedAvgReturn || 0;
});

const bannerData = computed(() => [
  {
    id: "netWorth",
    label: "Net Worth",
    value: convertToInr(health.value?.netWorth),
    tooltip: "Your total financial value (assets minus liabilities). Positive is healthy.",
    isHealthy: (health.value?.netWorth || 0) > 0,
  },
  {
    id: "netIncome",
    label: "Net Income",
    value: convertToInr(health.value?.netIncome),
    tooltip: "Money remaining after expenses and EMIs. Higher is better for savings.",
    isHealthy: (health.value?.netIncome || 0) > 0,
  },
  {
    id: "savingsRatio",
    label: "Savings Ratio",
    value: health.value?.savingsRatio,
    tooltip: "Percentage of income saved monthly. Aim for 20% or higher.",
    isHealthy: parseFloat(health.value?.savingsRatio || '0') >= 20,
  },
  {
    id: "debtRatio",
    label: "Debt to Income Ratio",
    value: `${((health.value?.debtToIncomeRatio || 0) * 100).toFixed(1)}%`,
    tooltip: "Portion of income for debt payments. Keep below 40% (lower is better).",
    isHealthy: (health.value?.debtToIncomeRatio || 0) < 0.4,
  },
]);
</script>

<template>
  <div class="mt-15">
    <span class="font-18 font-weight-700">Personal Finance Snapshot</span>
    <section
      class="w-100 mt-10 d-flex flex-direction-column input-shadow br-10 pv-10"
    >
      <div class="flex-1 d-flex">
        <template v-for="(item, index) in bannerData" :key="item.id">
          <div
            class="d-flex flex-direction-column justify-center pv-8 ph-5 gap-3 flex-1"
          >
            <span
              class="banner-label text-center font-weight-600 d-flex align-items-center justify-center gap-5"
            >
              {{ item.label }}
              <span
                class="health-indicator"
                :class="item.isHealthy ? 'indicator-healthy' : 'indicator-unhealthy'"
                :title="item.isHealthy ? 'Healthy' : 'Needs Attention'"
              ></span>
            </span>
            <span class="banner-value font-weight-500 text-center"
              >{{ item.value }}
            </span>
          </div>
          <span class="v-divider mv-8" v-if="index < bannerData.length - 1" :key="`divider-${item.id}`" />
        </template>
      </div>

      <!-- Collapsible Accordion for Tooltips -->
      <div class="accordion-section">
        <div
          class="accordion-header"
          @click="isAccordionExpanded = !isAccordionExpanded"
        >
          <span class="accordion-title">
            <i class="pi pi-info-circle"></i>
            Understanding Your Metrics
          </span>
          <i class="pi accordion-chevron" :class="isAccordionExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"></i>
        </div>
        <div class="accordion-content" v-show="isAccordionExpanded">
          <div
            v-for="item in bannerData"
            :key="item.id"
            class="metric-explanation"
          >
            <div class="metric-explanation-header">
              <span class="metric-name">{{ item.label }}</span>
              <span
                class="health-badge"
                :class="item.isHealthy ? 'badge-healthy' : 'badge-unhealthy'"
              >
                {{ item.isHealthy ? 'Healthy' : 'Needs Attention' }}
              </span>
            </div>
            <p class="metric-tooltip">{{ item.tooltip }}</p>
          </div>
        </div>
      </div>
    </section>

    <div class="chart-grid mt-20">
      <div class="chart-card">
        <span class="card-title">Emergency Fund Status</span>
        <div v-if="fundstatus" class="d-flex flex-direction-column align-items-center justify-center">
          <div class="barOverflow position-relative">
            <div
              class="bar"
              :style="{
                transform: `rotate(${45 + (emergencyFundPercentage * 1.8)}deg)`,
                borderBottomColor: emergencyFundColor,
                borderRightColor: emergencyFundColor,
              }"
            ></div>
            <div
              class="position-absolute l-0 r-0 b-0 m-auto w-max h-max d-flex flex-direction-column align-items-center"
            >
              <span class="gauge-value">{{ emergencyFundPercentage.toFixed(1) }}%</span>
              <span class="gauge-label">Achieved</span>
            </div>
          </div>
        </div>
        <div v-else class="d-flex flex-direction-column align-items-center justify-center p-20">
          <i class="pi pi-info-circle font-30 mb-10" style="color: var(--an-text-secondary);"></i>
          <span class="font-16 font-weight-500 text-center" style="color: var(--an-text-secondary);">
            Emergency fund information not available
          </span>
          <span class="font-14 text-center mt-5" style="color: var(--an-text-muted);">
            Please update your financial information to see your emergency fund status
          </span>
        </div>
        <span class="h-divider" v-if="fundstatus" />
        <div class="balance-grid" v-if="fundstatus">
          <div class="balance-item">
            <span class="balance-label">Recommended Balance</span>
            <span class="balance-value balance-recommended">{{
              convertToInr(recommendedEmergencyFund)
            }}</span>
          </div>
          <span class="v-divider" />
          <div class="balance-item">
            <span class="balance-label">Current Balance</span>
            <span
              class="balance-value"
              :style="{
                color:
                  fundstatus.currentBalance < recommendedEmergencyFund
                    ? 'var(--an-error)'
                    : 'var(--an-success)',
              }"
              >{{ convertToInr(fundstatus.currentBalance) }}</span
            >
          </div>
        </div>
      </div>
      <!-- Insurance Coverage Status Card -->
      <div class="chart-card">
        <span class="card-title">Insurance Coverage Status</span>
        <div class="insurance-content">
          <!-- Term Life Insurance -->
          <div class="insurance-item">
            <div class="insurance-header">
              <span class="insurance-type">Term Life Insurance</span>
              <span
                class="adequacy-badge"
                :class="isTermLifeAdequate ? 'badge-adequate' : 'badge-inadequate'"
              >
                {{ isTermLifeAdequate ? 'Adequate' : 'Inadequate' }}
              </span>
            </div>
            <div class="coverage-grid">
              <div class="coverage-item">
                <span class="coverage-label">Current Coverage</span>
                <span
                  class="coverage-value"
                  :class="isTermLifeAdequate ? 'value-adequate' : 'value-inadequate'"
                >
                  {{ convertToInr(currentTermLifeCoverage) }}
                </span>
              </div>
              <span class="coverage-divider">vs</span>
              <div class="coverage-item">
                <span class="coverage-label">Recommended</span>
                <span class="coverage-value coverage-recommended">
                  {{ convertToInr(recommendedTermLifeCoverage) }}
                </span>
              </div>
            </div>
          </div>

          <span class="h-divider" />

          <!-- Health Insurance -->
          <div class="insurance-item">
            <div class="insurance-header">
              <span class="insurance-type">Health Insurance</span>
              <span
                class="adequacy-badge"
                :class="isHealthAdequate ? 'badge-adequate' : 'badge-inadequate'"
              >
                {{ isHealthAdequate ? 'Adequate' : 'Inadequate' }}
              </span>
            </div>
            <div class="coverage-grid">
              <div class="coverage-item">
                <span class="coverage-label">Current Coverage</span>
                <span
                  class="coverage-value"
                  :class="isHealthAdequate ? 'value-adequate' : 'value-inadequate'"
                >
                  {{ convertToInr(currentHealthCoverage) }}
                </span>
              </div>
              <span class="coverage-divider">vs</span>
              <div class="coverage-item">
                <span class="coverage-label">Recommended</span>
                <span class="coverage-value coverage-recommended">
                  {{ convertToInr(recommendedHealthCoverage) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Calculations Accordion Row -->
    <div class="calculations-section mt-20">
      <div class="calculations-card">
        <div
          class="calculations-accordion-header"
          @click="isCalculationsAccordionExpanded = !isCalculationsAccordionExpanded"
        >
          <span class="accordion-title">
            <i class="pi pi-calculator"></i>
            How are these calculated?
          </span>
          <i
            class="pi accordion-chevron"
            :class="isCalculationsAccordionExpanded ? 'pi-chevron-up' : 'pi-chevron-down'"
          ></i>
        </div>
        <div class="calculations-accordion-content" v-show="isCalculationsAccordionExpanded">
          <div class="calc-grid">
            <!-- Emergency Fund Calculation -->
            <div class="calculation-section">
              <h5 class="calc-heading">
                <i class="pi pi-shield"></i>
                Emergency Fund
              </h5>
              <div class="calc-formula">
                <div class="calc-item">
                  <span class="calc-label">Monthly Expenses:</span>
                  <span class="calc-value">
                    {{ convertToInr(totalExpense) }}
                  </span>
                </div>
                <div class="calc-item">
                  <span class="calc-label">+ Monthly EMI (Active Loans):</span>
                  <span class="calc-value">
                    {{ convertToInr(monthlyEMI) }}
                  </span>
                </div>
                <div class="calc-item">
                  <span class="calc-label">= Total Monthly Outflow:</span>
                  <span class="calc-value">
                    {{ convertToInr(totalExpense + monthlyEMI) }}
                  </span>
                </div>
                <div class="calc-item calc-total">
                  <span class="calc-label">× 12 Months = Recommended Fund:</span>
                  <span class="calc-value">{{ convertToInr(recommendedEmergencyFund) }}</span>
                </div>
              </div>
              <p class="calc-note">
                <i class="pi pi-info-circle"></i>
                Your emergency fund should cover 12 months of total outflows (expenses + EMI obligations).
              </p>
            </div>

            <!-- Term Life Calculation -->
            <div class="calculation-section">
              <h5 class="calc-heading">
                <i class="pi pi-user"></i>
                Term Life Coverage
              </h5>
              <div class="calc-formula">
                <div class="calc-item">
                  <span class="calc-label">Annual Expenses × Years to Retirement:</span>
                  <span class="calc-value">
                    {{ convertToInr(totalExpense * 12) }} × {{ yearsOfDependency }} years
                  </span>
                </div>
                <div class="calc-item">
                  <span class="calc-label">+ Outstanding Liabilities:</span>
                  <span class="calc-value">{{ convertToInr(totalLiability) }}</span>
                </div>
                <div class="calc-item">
                  <span class="calc-label">+ Goals Target Amount:</span>
                  <span class="calc-value">{{ convertToInr(totalGoalsTarget) }}</span>
                </div>
                <div class="calc-item">
                  <span class="calc-label">− Existing Assets:</span>
                  <span class="calc-value">{{ convertToInr(totalAsset) }}</span>
                </div>
                <div class="calc-item calc-total">
                  <span class="calc-label">= Recommended Coverage:</span>
                  <span class="calc-value">{{ convertToInr(recommendedTermLifeCoverage) }}</span>
                </div>
              </div>
              <p class="calc-note">
                <i class="pi pi-info-circle"></i>
                Term life insurance should cover your family's expenses until retirement, plus debts and goals. Retirement @65 years.
              </p>
            </div>

            <!-- Health Insurance Calculation -->
            <div class="calculation-section">
              <h5 class="calc-heading">
                <i class="pi pi-heart"></i>
                Health Coverage
              </h5>
              <div class="calc-formula">
                <div class="calc-item">
                  <span class="calc-label">Marital Status:</span>
                  <span class="calc-value">
                    <strong>{{ store.dashboardData?.maritalStatus || 'Single' }}</strong>
                  </span>
                </div>
                <div class="calc-item calc-total">
                  <span class="calc-label">Recommended Coverage:</span>
                  <span class="calc-value">
                    {{ convertToInr(recommendedHealthCoverage) }}
                  </span>
                </div>
              </div>
              <p class="calc-note">
                <i class="pi pi-info-circle"></i>
                {{ store.dashboardData?.maritalStatus === 'Single'
                  ? 'Singles should have at least ₹10 lakhs coverage for medical emergencies.'
                  : 'Families should have at least ₹15 lakhs coverage to protect all members.' }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Compact SIP Information Row -->
    <div class="sip-info-row mt-15">
      <div class="sip-info-content">
        <div class="sip-info-item">
          <span class="sip-info-label">
            Monthly SIP
            <i
              class="pi pi-info-circle"
              v-tooltip.top="'Total consolidated monthly SIP investments including Mutual Funds, LIC Premiums, EPF, and other systematic investments'"
            ></i>
          </span>
          <span class="sip-info-value">{{ convertToInr(currentMonthlySIP) }}</span>
        </div>
        <span class="sip-divider"></span>
        <div class="sip-info-item">
          <span class="sip-info-label">
            Weighted Avg Return
            <i
              class="pi pi-info-circle"
              v-tooltip.top="'Average expected annual return across all your SIP investments (in %)'"
            ></i>
          </span>
          <span class="sip-info-value">{{ currentWeightedAvgReturn.toFixed(1) }}%</span>
        </div>
        <button class="sip-modify-btn" @click="openSIPDialog">
          <i class="pi pi-pencil"></i>
          Modify
        </button>
      </div>
    </div>

    <!-- SIP Management Dialog -->
    <ManageSIPDialog
      v-model="isSIPDialogVisible"
      :currentMonthlySIP="currentMonthlySIP"
      :currentWeightedAvgReturn="currentWeightedAvgReturn"
      @update:modelValue="closeSIPDialog"
    />
  </div>
</template>

<style scoped>
/* Chart Grid Layout */
.chart-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  max-width: 100%;
}

.card-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.chart-container {
  width: 100%;
  height: 180px;
  max-width: 100%;
  position: relative;
}

.chart-container canvas {
  max-width: 100% !important;
  height: 100% !important;
}

@media (max-width: 1024px) {
  .chart-grid {
    grid-template-columns: 1fr;
  }

  .chart-container {
    height: 200px;
  }
}

.snapshot-grid {
  background-color: var(--an-gray1);
  gap: 3px;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: repeat(2, 1fr);
}

.barOverflow {
  overflow: hidden;
  width: 160px;
  height: 80px;
}
.bar {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 12px solid var(--an-background-light);
}

/* Gauge Text */
.gauge-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
}

.gauge-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

/* Balance Grid */
.balance-grid {
  display: flex;
  align-items: center;
  gap: 12px;
}

.balance-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  gap: 4px;
}

.balance-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
}

.balance-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
}

.balance-recommended {
  color: #10b981;
}

/* Health Indicators */
.health-indicator {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.indicator-healthy {
  background-color: #10b981;
}

.indicator-unhealthy {
  background-color: #ef4444;
}

/* Banner Data Styling */
.banner-label {
  font-size: 0.8125rem;
}

.banner-value {
  font-size: 1.125rem;
}

.pv-8 {
  padding-top: 8px;
  padding-bottom: 8px;
}

.ph-5 {
  padding-left: 5px;
  padding-right: 5px;
}

.gap-3 {
  gap: 3px;
}

.mv-8 {
  margin-top: 8px;
  margin-bottom: 8px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .banner-label {
    font-size: 0.8rem;
  }

  .banner-value {
    font-size: 1.05rem;
  }

  .card-title {
    font-size: 0.95rem;
  }
}

@media (max-width: 992px) {
  .chart-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .calc-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .mt-15 {
    margin-top: 12px;
  }

  .mt-20 {
    margin-top: 16px;
  }

  .font-18 {
    font-size: 16px;
  }

  /* Banner - Switch to vertical layout */
  .flex-1.d-flex {
    flex-direction: column;
    gap: 0;
  }

  .flex-1.d-flex > .d-flex.flex-direction-column {
    padding: 12px 8px;
  }

  .v-divider.mv-8 {
    display: none;
  }

  /* Add horizontal dividers instead */
  .flex-1.d-flex > .d-flex.flex-direction-column:not(:last-child) {
    border-bottom: 1px solid #e5e7eb;
  }

  .banner-label {
    font-size: 0.75rem;
  }

  .banner-value {
    font-size: 1rem;
  }

  .health-indicator {
    width: 6px;
    height: 6px;
  }

  /* Chart Cards */
  .chart-card {
    padding: 12px;
  }

  .card-title {
    font-size: 0.9rem;
  }

  .chart-container {
    height: 160px;
  }

  /* Emergency Fund Gauge */
  .barOverflow {
    width: 140px;
    height: 70px;
  }

  .bar {
    width: 140px;
    height: 140px;
    border-width: 10px;
  }

  .gauge-value {
    font-size: 1rem;
  }

  .gauge-label {
    font-size: 0.7rem;
  }

  /* Balance Grid */
  .balance-grid {
    flex-direction: column;
    gap: 8px;
  }

  .balance-grid .v-divider {
    display: none;
  }

  .balance-item {
    width: 100%;
    padding: 8px;
    background: #f8fafc;
    border-radius: 6px;
  }

  .balance-label {
    font-size: 0.75rem;
  }

  .balance-value {
    font-size: 1rem;
  }

  /* Insurance Coverage */
  .insurance-type {
    font-size: 0.8rem;
  }

  .adequacy-badge {
    font-size: 0.65rem;
    padding: 3px 8px;
  }

  .coverage-grid {
    flex-direction: column;
    gap: 8px;
    padding: 8px;
  }

  .coverage-divider {
    display: none;
  }

  .coverage-item {
    width: 100%;
    padding: 8px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
  }

  .coverage-label {
    font-size: 0.7rem;
  }

  .coverage-value {
    font-size: 0.9rem;
  }

  /* Accordion */
  .accordion-header {
    padding: 10px;
  }

  .accordion-title {
    font-size: 0.8rem;
  }

  .accordion-title i {
    font-size: 0.8rem;
  }

  .accordion-content {
    padding: 10px;
    gap: 10px;
  }

  .metric-explanation {
    padding: 10px;
  }

  .metric-name {
    font-size: 0.8rem;
  }

  .metric-tooltip {
    font-size: 0.75rem;
  }

  /* Calculations */
  .calculations-accordion-header {
    padding: 10px 12px;
  }

  .calculations-accordion-header .accordion-title {
    font-size: 0.85rem;
  }

  .calculations-accordion-content {
    padding: 12px;
  }

  .calc-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .font-18 {
    font-size: 14px;
  }

  .pv-10 {
    padding-top: 8px;
    padding-bottom: 8px;
  }

  /* Banner */
  .flex-1.d-flex > .d-flex.flex-direction-column {
    padding: 10px 6px;
  }

  .banner-label {
    font-size: 0.7rem;
    gap: 3px;
  }

  .banner-value {
    font-size: 0.9rem;
  }

  .health-indicator {
    width: 5px;
    height: 5px;
  }

  /* Chart Cards */
  .chart-card {
    padding: 10px;
    gap: 8px;
  }

  .card-title {
    font-size: 0.85rem;
  }

  /* Emergency Fund Gauge */
  .barOverflow {
    width: 120px;
    height: 60px;
  }

  .bar {
    width: 120px;
    height: 120px;
    border-width: 8px;
  }

  .gauge-value {
    font-size: 0.9rem;
  }

  .gauge-label {
    font-size: 0.65rem;
  }

  .balance-item {
    padding: 6px;
  }

  .balance-label {
    font-size: 0.7rem;
  }

  .balance-value {
    font-size: 0.9rem;
  }

  /* Insurance */
  .insurance-item {
    gap: 6px;
  }

  .insurance-type {
    font-size: 0.75rem;
  }

  .adequacy-badge {
    font-size: 0.6rem;
    padding: 2px 6px;
  }

  .coverage-grid {
    padding: 6px;
    gap: 6px;
  }

  .coverage-item {
    padding: 6px;
  }

  .coverage-label {
    font-size: 0.65rem;
  }

  .coverage-value {
    font-size: 0.85rem;
  }

  /* Accordion */
  .accordion-header,
  .calculations-accordion-header {
    padding: 8px;
  }

  .accordion-title {
    font-size: 0.75rem;
    gap: 6px;
  }

  .accordion-title i {
    font-size: 0.75rem;
  }

  .accordion-chevron {
    font-size: 0.7rem;
  }

  .accordion-content,
  .calculations-accordion-content {
    padding: 8px;
    gap: 8px;
  }

  .metric-explanation {
    padding: 8px;
  }

  .metric-name {
    font-size: 0.75rem;
  }

  .health-badge {
    font-size: 0.6rem;
    padding: 2px 6px;
  }

  .metric-tooltip {
    font-size: 0.7rem;
  }

  /* Calculations */
  .calculations-accordion-header .accordion-title {
    font-size: 0.8rem;
  }

  .calc-grid {
    gap: 10px;
  }

  .calculation-section {
    padding: 8px;
    border-left-width: 3px;
  }

  .calc-heading {
    font-size: 0.75rem;
    margin-bottom: 6px;
  }

  .calc-heading i {
    font-size: 0.8rem;
  }

  .calc-formula {
    gap: 5px;
    margin-bottom: 8px;
  }

  .calc-item {
    padding: 4px 6px;
    font-size: 0.6rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  .calc-label,
  .calc-value {
    text-align: left;
    white-space: normal;
  }

  .calc-value {
    font-size: 0.7rem;
  }

  .calc-note {
    font-size: 0.65rem;
    padding: 5px 6px;
    gap: 4px;
  }

  .calc-note i {
    font-size: 0.7rem;
  }
}

@media (max-width: 360px) {
  .font-18 {
    font-size: 13px;
  }

  .mt-15,
  .mt-20 {
    margin-top: 10px;
  }

  /* Banner */
  .flex-1.d-flex > .d-flex.flex-direction-column {
    padding: 8px 5px;
  }

  .banner-label {
    font-size: 0.65rem;
  }

  .banner-value {
    font-size: 0.85rem;
  }

  /* Chart Cards */
  .chart-card {
    padding: 8px;
  }

  .card-title {
    font-size: 0.8rem;
  }

  /* Emergency Fund */
  .barOverflow {
    width: 100px;
    height: 50px;
  }

  .bar {
    width: 100px;
    height: 100px;
    border-width: 6px;
  }

  .gauge-value {
    font-size: 0.8rem;
  }

  .gauge-label {
    font-size: 0.6rem;
  }

  .balance-label {
    font-size: 0.65rem;
  }

  .balance-value {
    font-size: 0.85rem;
  }

  /* Insurance */
  .insurance-type {
    font-size: 0.7rem;
  }

  .coverage-label {
    font-size: 0.6rem;
  }

  .coverage-value {
    font-size: 0.8rem;
  }

  /* Accordion */
  .accordion-title {
    font-size: 0.7rem;
  }

  .metric-name {
    font-size: 0.7rem;
  }

  .metric-tooltip {
    font-size: 0.65rem;
  }

  /* Calculations */
  .calc-heading {
    font-size: 0.7rem;
  }

  .calc-item {
    font-size: 0.6rem;
  }

  .calc-value {
    font-size: 0.65rem;
  }

  .calc-note {
    font-size: 0.6rem;
  }
}

/* Accordion Section */
.accordion-section {
  margin-top: 10px;
  border-top: 1px solid #e5e7eb;
  padding-top: 0;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 6px;
}

.accordion-header:hover {
  background-color: #f8fafc;
}

.accordion-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #475569;
}

.accordion-title i {
  font-size: 0.875rem;
  color: #3b82f6;
}

.accordion-chevron {
  font-size: 0.75rem;
  color: #64748b;
  transition: transform 0.2s ease;
}

.accordion-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideDown 0.2s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-explanation {
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #e2e8f0;
}

.metric-explanation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.metric-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.health-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-healthy {
  background-color: #d1fae5;
  color: #065f46;
}

.badge-unhealthy {
  background-color: #fee2e2;
  color: #991b1b;
}

.metric-tooltip {
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.5;
  margin: 0;
}

/* Insurance Coverage Card Styles */
.insurance-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.insurance-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.insurance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.insurance-type {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e293b;
}

.adequacy-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-adequate {
  background: #d1fae5;
  color: #065f46;
}

.badge-inadequate {
  background: #fee2e2;
  color: #991b1b;
}

.coverage-grid {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: #f8fafc;
  border-radius: 8px;
}

.coverage-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.coverage-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
}

.coverage-value {
  font-size: 1rem;
  font-weight: 700;
  color: #1e293b;
}

.value-adequate {
  color: #059669;
}

.value-inadequate {
  color: #dc2626;
}

.coverage-recommended {
  color: #3b82f6;
}

.coverage-divider {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  padding: 0 4px;
}

.insurance-accordion {
  margin-top: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 10px;
}

.insurance-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 6px;
}

.insurance-accordion-header:hover {
  background-color: #f8fafc;
}

.insurance-accordion-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: slideDown 0.2s ease;
  max-height: 300px;
  overflow-y: auto;
}

/* Calculations Section Styles */
.calculations-section {
  width: 100%;
}

.calculations-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.calculations-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-bottom: 1px solid #bfdbfe;
}

.calculations-accordion-header:hover {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
}

.calculations-accordion-header .accordion-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1e40af;
}

.calculations-accordion-header .accordion-title i {
  font-size: 1rem;
}

.calculations-accordion-content {
  padding: 16px;
  animation: slideDown 0.2s ease;
}

.calc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

@media (max-width: 1024px) {
  .calc-grid {
    grid-template-columns: 1fr;
  }
}

.calculation-section {
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.calc-heading {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px 0;
}

.calc-heading i {
  color: #3b82f6;
  font-size: 0.9rem;
}

.calc-formula {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
}

.calc-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: white;
  border-radius: 4px;
  font-size: 0.75rem;
  gap: 8px;
}

.calc-label {
  color: #64748b;
  font-weight: 500;
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
}

.calc-value {
  color: #1e293b;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}

.calc-item.calc-total {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 1px solid #93c5fd;
  margin-top: 4px;
}

.calc-item.calc-total .calc-label,
.calc-item.calc-total .calc-value {
  color: #1e40af;
  font-weight: 700;
}

.calc-note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.75rem;
  color: #475569;
  line-height: 1.4;
  margin: 0;
  padding: 8px 10px;
  background: white;
  border-radius: 4px;
  border-left: 3px solid #f59e0b;
}

.calc-note i {
  color: #f59e0b;
  font-size: 0.8rem;
  margin-top: 1px;
  flex-shrink: 0;
}

/* Compact SIP Information Row */
.sip-info-row {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 8px 12px;
}

.sip-info-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.sip-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sip-info-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
}

.sip-info-label i {
  font-size: 0.7rem;
  color: #94a3b8;
  cursor: help;
}

.sip-info-value {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1e293b;
}

.sip-divider {
  width: 1px;
  height: 32px;
  background: #e5e7eb;
}

.sip-modify-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: var(--an-primary);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.sip-modify-btn:hover {
  background: var(--an-primary-dark, var(--an-primary));
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.sip-modify-btn i {
  font-size: 0.75rem;
}

/* Responsive for SIP Row */
@media (max-width: 768px) {
  .sip-info-content {
    flex-wrap: wrap;
    gap: 10px;
  }

  .sip-info-item {
    flex: 1;
    min-width: 100px;
  }

  .sip-divider {
    display: none;
  }

  .sip-modify-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
